import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

const DOCS_URL = 'https://docs.metha.life/';
const PIN_KEY = 'metha-admin:sidebar-pinned';

type IconProps = { className?: string };

/* Lucide-style outline icons (MIT). Inline to avoid pulling in lucide-react
 * for just 8 glyphs — keeps the bundle lean and lets us tune stroke width
 * to match the terminal aesthetic. */
const Icon = {
  Overview: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1"/>
      <rect x="14" y="3" width="7" height="5" rx="1"/>
      <rect x="14" y="12" width="7" height="9" rx="1"/>
      <rect x="3" y="16" width="7" height="5" rx="1"/>
    </svg>
  ),
  Auction: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="m14 13-7.5 7.5a2.12 2.12 0 0 1-3-3L11 10"/>
      <path d="m16 16 6-6"/>
      <path d="m8 8 6-6"/>
      <path d="m9 7 8 8"/>
      <path d="m21 11-8-8"/>
    </svg>
  ),
  Auditing: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <path d="M14 2v6h6"/>
      <path d="m9 15 2 2 4-4"/>
    </svg>
  ),
  Auditors: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Governance: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 12 2 2 4-4"/>
      <path d="M5 7v10a4 4 0 0 0 4 4h6a4 4 0 0 0 4-4V7"/>
      <path d="M3 7h18l-2-4H5L3 7Z"/>
    </svg>
  ),
  Compliance: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
      <path d="m9 12 2 2 4-4"/>
    </svg>
  ),
  Account: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Docs: (p: IconProps) => (
    <svg className={p.className} width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  Pin: (p: IconProps) => (
    <svg className={p.className} width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 17v5"/>
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/>
    </svg>
  ),
};

type RouteDef = { to: string; label: string; icon: (p: IconProps) => ReactNode };

const routes: RouteDef[] = [
  { to: '/',           label: 'Overview',   icon: Icon.Overview   },
  { to: '/auction',    label: 'Auction',    icon: Icon.Auction    },
  { to: '/auditing',   label: 'Auditing',   icon: Icon.Auditing   },
  { to: '/auditors',   label: 'Auditors',   icon: Icon.Auditors   },
  { to: '/governance', label: 'Governance', icon: Icon.Governance },
  { to: '/compliance', label: 'Compliance', icon: Icon.Compliance },
  { to: '/account',    label: 'Account',    icon: Icon.Account    },
];

export type SidebarProps = {
  /** Open state for the mobile drawer (overlays as a full panel <=900px). */
  mobileOpen: boolean;
  onMobileClose: () => void;
};

export default function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  // Persisted "pin" state — when pinned, the rail stays expanded.
  // When not, it stays narrow (56px) but expands on hover via CSS.
  const [pinned, setPinned] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(PIN_KEY) === '1';
  });

  useEffect(() => {
    try { window.localStorage.setItem(PIN_KEY, pinned ? '1' : '0'); } catch { /* ignore */ }
  }, [pinned]);

  // Reflect the pinned state on <body> so the main column can shift its
  // padding-left from rail to expanded width without prop drilling.
  useEffect(() => {
    if (pinned) document.body.setAttribute('data-sidebar', 'pinned');
    else document.body.removeAttribute('data-sidebar');
    return () => document.body.removeAttribute('data-sidebar');
  }, [pinned]);

  // ESC closes the mobile drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onMobileClose(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen, onMobileClose]);

  const cls =
    'sidebar' +
    (pinned ? ' is-pinned' : '') +
    (mobileOpen ? ' is-mobile-open' : '');

  return (
    <>
      {/* Mobile-only backdrop. Hidden on desktop via CSS. */}
      <div
        className={`sidebar-backdrop${mobileOpen ? ' open' : ''}`}
        onClick={onMobileClose}
        aria-hidden={!mobileOpen}
      />

      <aside className={cls} aria-label="Primary navigation">
        <nav className="sidebar-links">
          {routes.map((r) => (
            <NavLink
              key={r.to}
              to={r.to}
              end={r.to === '/'}
              className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
              onClick={onMobileClose}
              title={r.label}
            >
              <span className="sidebar-link-icon"><r.icon /></span>
              <span className="sidebar-link-label">{r.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          <a
            className="sidebar-link sidebar-link--ext"
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onMobileClose}
            title="Open Metha docs in a new tab"
          >
            <span className="sidebar-link-icon"><Icon.Docs /></span>
            <span className="sidebar-link-label">Docs ↗</span>
          </a>

          <button
            type="button"
            className={'sidebar-pin' + (pinned ? ' is-on' : '')}
            onClick={() => setPinned((v) => !v)}
            title={pinned ? 'Unpin sidebar (auto-collapse)' : 'Pin sidebar (keep expanded)'}
            aria-pressed={pinned}
          >
            <Icon.Pin />
            <span className="sidebar-link-label">{pinned ? 'Unpin' : 'Pin'}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
